import { useEffect, useRef } from 'react';

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

export const logAnalyticsEvent = (eventName: string, params: Record<string, any> = {}) => {
  if (typeof window === 'undefined') return;

  if (window.gtag) {
    window.gtag('event', eventName, params);
  } else if (window.dataLayer) {
    window.dataLayer.push({ event: eventName, ...params });
  } else {
    console.log('[Analytics Fallback]', eventName, params);
  }
};

/**
 * Встановлює GA4 User ID для зв'язку браузерної і PWA standalone сесій.
 *
 * ВАЖЛИВО: Викликати ДО будь-яких інших GA4-подій в сесії (page_view, session_start тощо),
 * інакше GA4 не встигне прив'язати події до user_id.
 *
 * Потрібне налаштування в GA4 Admin: Reporting Identity → "By User ID, then Device".
 *
 * @see https://developers.google.com/analytics/devguides/collection/ga4/user-id
 */
export const setGa4UserId = (userId: number) => {
  if (typeof window === 'undefined') return;
  const id = String(userId);

  if (window.gtag) {
    // Прямий GA4 тег — встановлює user_id для всіх наступних подій в сесії
    window.gtag('set', { user_id: id });
  } else if (window.dataLayer) {
    // GTM: User-ID variable в GTM має читати поле user_id з dataLayer
    window.dataLayer.push({ user_id: id });
  }
};

/**
 * Хук для відстеження PWA-специфічних аналітичних подій.
 *
 * @param userId — внутрішній ID юзера з БД (User.id). Передається ззовні щоб
 *   гарантувати правильний порядок: спочатку setGa4UserId, потім pwa_session_start.
 *   Якщо userId ще невідомий (юзер не авторизований) — pwa_session_start не стріляє
 *   до отримання ID, що коректно для анонімних сесій.
 */
export const usePwaAnalytics = (userId?: number) => {
  const hasLoggedStart = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Перевіряємо standalone-режим: display-mode для Chrome/Android та navigator.standalone для iOS Safari
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      ('standalone' in navigator && (navigator as any).standalone === true);

    // 1. Log Session Start in Standalone mode + встановлюємо cookie
    //    ПОРЯДОК КРИТИЧНИЙ: спочатку setGa4UserId, потім pwa_session_start
    if (!hasLoggedStart.current && isStandalone) {
      // Якщо userId відомий — встановлюємо ДО pwa_session_start
      if (userId) {
        setGa4UserId(userId);
      }
      logAnalyticsEvent('pwa_session_start', { method: 'standalone' });
      // Cookie для надійного трекінгу продажів на бекенді (WC checkout)
      // max-age = 4 години — достатньо для сесії замовлення, але не залишається на 30 днів
      document.cookie = 'pwa_session=1; path=/; max-age=14400; SameSite=Lax';
      hasLoggedStart.current = true;
    } else if (!isStandalone) {
      // Якщо відкрито НЕ в PWA — видаляємо cookie, щоб не позначити звичайне
      // WooCommerce-замовлення як PWA (саме це і спричиняло помилкову мітку)
      document.cookie = 'pwa_session=0; path=/; max-age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax';
    }

    // 2. Слухаємо зміну display-mode (напр. якщо користувач виходить зі standalone)
    const mq = window.matchMedia('(display-mode: standalone)');
    const handleDisplayModeChange = (e: MediaQueryListEvent) => {
      if (!e.matches) {
        // Вийшли зі standalone — одразу очищуємо cookie
        document.cookie = 'pwa_session=0; path=/; max-age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax';
      }
    };
    mq.addEventListener('change', handleDisplayModeChange);

    // 3. Track Install Prompt
    const handleBeforeInstallPrompt = (e: Event) => {
      logAnalyticsEvent('pwa_install_prompt_eligible');
    };

    // 4. Track App Installed
    const handleAppInstalled = (e: Event) => {
      logAnalyticsEvent('pwa_installed');
    };

    // 5. Track Offline/Online Mode
    const handleOffline = () => {
      logAnalyticsEvent('pwa_went_offline');
    };

    const handleOnline = () => {
      logAnalyticsEvent('pwa_came_online');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    return () => {
      mq.removeEventListener('change', handleDisplayModeChange);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, [userId]);
};
